import { useState } from 'react'
import { Button, Input, InputNumber, message, Radio, Segmented, Select, Space, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { downloadTextFile, generateBatchData } from '@/api/dataFactory'
import type { BatchDataResult, BatchFieldRule } from '@/types'

/** 字段参数元信息（kind 决定渲染控件） */
interface FieldParamMeta {
  key: string
  label: string
  kind: 'text' | 'number' | 'select'
  options?: string[]
  placeholder?: string
  default?: string | number
}

/** 字段规则类型元信息（params 顺序即渲染顺序） */
const FIELD_TYPES: { value: string; label: string; params: FieldParamMeta[] }[] = [
  { value: 'constant', label: '固定值', params: [{ key: 'value', label: '值', kind: 'text', default: '' }] },
  {
    value: 'sequence', label: '自增序号', params: [
      { key: 'prefix', label: '前缀', kind: 'text', default: '' },
      { key: 'start', label: '起始', kind: 'number', default: 1 },
      { key: 'step', label: '步长', kind: 'number', default: 1 },
      { key: 'padding', label: '补零位数', kind: 'number', default: 0 },
    ],
  },
  {
    value: 'random_int', label: '随机整数', params: [
      { key: 'min', label: '最小', kind: 'number', default: 0 },
      { key: 'max', label: '最大', kind: 'number', default: 100 },
    ],
  },
  {
    value: 'random_float', label: '随机小数', params: [
      { key: 'min', label: '最小', kind: 'number', default: 0 },
      { key: 'max', label: '最大', kind: 'number', default: 100 },
      { key: 'precision', label: '小数位', kind: 'number', default: 2 },
    ],
  },
  {
    value: 'random_string', label: '随机字符串', params: [
      { key: 'length', label: '长度', kind: 'number', default: 8 },
      { key: 'charset', label: '字符集', kind: 'select', options: ['alnum', 'letters', 'digits', 'upper', 'lower'], default: 'alnum' },
    ],
  },
  { value: 'enum', label: '枚举随机', params: [{ key: 'candidates', label: '候选值', kind: 'text', placeholder: '逗号分隔，如 A,B,C', default: '' }] },
  { value: 'uuid', label: 'UUID', params: [] },
  {
    value: 'date', label: '随机日期', params: [
      { key: 'start', label: '开始', kind: 'text', placeholder: '2024-01-01', default: '2024-01-01' },
      { key: 'end', label: '结束', kind: 'text', placeholder: '2026-12-31', default: '2026-12-31' },
    ],
  },
  {
    value: 'datetime', label: '随机日期时间', params: [
      { key: 'start', label: '开始', kind: 'text', placeholder: '2024-01-01 00:00:00', default: '2024-01-01 00:00:00' },
      { key: 'end', label: '结束', kind: 'text', placeholder: '2026-12-31 23:59:59', default: '2026-12-31 23:59:59' },
    ],
  },
  { value: 'phone', label: '随机手机号', params: [] },
  { value: 'id_card', label: '随机身份证', params: [] },
  { value: 'name', label: '随机姓名', params: [] },
  { value: 'email', label: '随机邮箱', params: [] },
  { value: 'bank_card', label: '随机银行卡', params: [] },
  { value: 'address', label: '随机地址', params: [] },
  { value: 'company', label: '公司名称', params: [] },
  { value: 'uscc', label: '营业执照编号', params: [] },
]

const findTypeMeta = (type: string) => FIELD_TYPES.find((t) => t.value === type)

/** 按类型默认值构造 params（切换类型时重置） */
const defaultParams = (type: string): Record<string, string | number> => {
  const meta = findTypeMeta(type)
  const params: Record<string, string | number> = {}
  meta?.params.forEach((p) => {
    params[p.key] = p.default ?? ''
  })
  return params
}

/** 单行初始值 */
const newRow = (): BatchFieldRule => ({ name: `field_${Date.now() % 1000}`, type: 'random_int', params: defaultParams('random_int') })

/** 批量参数化造数页签：字段规则 → N 行 CSV/JSON，直接喂给接口/UI 用例做数据驱动 */
export default function BatchDataTab() {
  const [fields, setFields] = useState<BatchFieldRule[]>([
    { name: 'username', type: 'name', params: {} },
    { name: 'mobile', type: 'phone', params: {} },
    { name: 'amount', type: 'random_float', params: { min: 1, max: 999, precision: 2 } },
  ])
  const [count, setCount] = useState(100)
  const [format, setFormat] = useState<'csv' | 'json'>('csv')
  const [result, setResult] = useState<BatchDataResult | null>(null)
  const [resultView, setResultView] = useState<'table' | 'text'>('table')
  const [loading, setLoading] = useState(false)

  /** 更新某个字段的属性 */
  const updateField = (index: number, patch: Partial<BatchFieldRule>) => {
    setFields((prev) => prev.map((f, i) => (i === index ? { ...f, ...patch } : f)))
  }

  /** 切换规则类型时重置参数为该类型默认值 */
  const handleTypeChange = (index: number, type: string) => {
    updateField(index, { type, params: defaultParams(type) })
  }

  /** 执行造数 */
  const handleGenerate = async () => {
    if (!fields.length) {
      message.warning('请至少添加一个字段规则')
      return
    }
    if (fields.some((f) => !f.name.trim())) {
      message.warning('存在未命名的字段')
      return
    }
    setLoading(true)
    try {
      const res = await generateBatchData({
        fields: fields.map((f) => ({ name: f.name.trim(), type: f.type, params: f.params })),
        count,
        format,
      })
      setResult(res)
      message.success(`已生成 ${res.row_count} 行数据`)
    } finally {
      setLoading(false)
    }
  }

  /** 复制/下载结果文本 */
  const handleCopy = async () => {
    if (!result) return
    await navigator.clipboard.writeText(result.content)
    message.success('已复制到剪贴板')
  }
  const handleDownload = () => {
    if (!result) return
    downloadTextFile(`auroratest-data.${format}`, result.content)
    message.success('已下载')
  }

  /** 预览表动态列 */
  const previewColumns: ColumnsType<Record<string, string>> =
    result?.columns.map((col) => ({
      title: col,
      dataIndex: col,
      ellipsis: { showTitle: true },
      render: (v: string) => <span style={{ fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 }}>{v}</span>,
    })) ?? []

  const previewData = result?.rows.map((row, idx) =>
    Object.fromEntries(result.columns.map((c, i) => [c, row[i]])),
  ) ?? []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 12, padding: '0 22px 12px' }}>
      {/* 字段规则编辑区 */}
      <div style={{ flexShrink: 0 }}>
        {fields.map((field, index) => {
          const meta = findTypeMeta(field.type)
          return (
            <Space key={index} wrap style={{ display: 'flex', marginBottom: 8 }} size={8}>
              <Input
                placeholder="字段名"
                value={field.name}
                onChange={(e) => updateField(index, { name: e.target.value })}
                style={{ width: 160 }}
              />
              <Select
                value={field.type}
                onChange={(v) => handleTypeChange(index, v)}
                options={FIELD_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                style={{ width: 130 }}
                showSearch
              />
              {meta?.params.map((p) =>
                p.kind === 'number' ? (
                  <InputNumber
                    key={p.key}
                    addonBefore={p.label}
                    value={Number(field.params[p.key] ?? p.default ?? 0)}
                    onChange={(v) => updateField(index, { params: { ...field.params, [p.key]: v ?? 0 } })}
                    style={{ width: 150 }}
                  />
                ) : p.kind === 'select' ? (
                  <Select
                    key={p.key}
                    value={String(field.params[p.key] ?? p.default ?? '')}
                    onChange={(v) => updateField(index, { params: { ...field.params, [p.key]: v } })}
                    options={(p.options ?? []).map((o) => ({ value: o, label: o }))}
                    style={{ width: 110 }}
                  />
                ) : (
                  <Input
                    key={p.key}
                    addonBefore={p.label}
                    placeholder={p.placeholder}
                    value={String(field.params[p.key] ?? '')}
                    onChange={(e) => updateField(index, { params: { ...field.params, [p.key]: e.target.value } })}
                    style={{ width: 230 }}
                  />
                ),
              )}
              <Button type="link" danger onClick={() => setFields((prev) => prev.filter((_, i) => i !== index))}>
                删除
              </Button>
            </Space>
          )
        })}
        <Button
          type="dashed"
          onClick={() => setFields((prev) => [...prev, newRow()])}
          style={{ width: 140 }}
        >
          添加字段
        </Button>
      </div>

      {/* 执行栏：行数 / 格式 / 生成 */}
      <Space wrap style={{ flexShrink: 0 }}>
        <InputNumber min={1} max={5000} value={count} onChange={(v) => setCount(v || 1)} addonBefore="行数" style={{ width: 150 }} />
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          options={[{ value: 'csv', label: 'CSV' }, { value: 'json', label: 'JSON' }]}
        />
        <Button
          type="primary"
          style={{ background: 'var(--au-primary)', border: 'none' }}
          loading={loading}
          onClick={handleGenerate}
        >
          生成数据
        </Button>
      </Space>

      {/* 结果区：表格预览 / 文本内容 切换 */}
      {result && (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Space style={{ marginBottom: 8, flexShrink: 0 }}>
            <Segmented
              value={resultView}
              onChange={(v) => setResultView(v as 'table' | 'text')}
              options={[
                { value: 'table', label: '表格预览' },
                { value: 'text', label: '文本内容' },
              ]}
            />
            <span style={{ color: 'var(--au-slate-muted)', fontSize: 12 }}>{result.row_count} 行 × {result.columns.length} 列</span>
            <div style={{ flex: 1 }} />
            <Button size="small" onClick={handleCopy}>复制</Button>
            <Button size="small" onClick={handleDownload}>下载 {format.toUpperCase()}</Button>
          </Space>
          {resultView === 'table' ? (
            <Table
              size="small"
              columns={previewColumns}
              dataSource={previewData}
              rowKey={(_, idx) => String(idx)}
              pagination={{ pageSize: 50, size: 'small', showSizeChanger: false, showTotal: (t) => `共 ${t} 行` }}
              scroll={{ x: 'max-content', y: '100%' }}
              style={{ flex: 1, minHeight: 0 }}
            />
          ) : (
            <Input.TextArea
              readOnly
              value={result.content}
              style={{
                flex: 1,
                minHeight: 0,
                fontFamily: "'JetBrains Mono', 'Consolas', monospace",
                fontSize: 12,
                background: 'var(--au-bg-cream)',
                resize: 'none',
              }}
            />
          )}
        </div>
      )}
    </div>
  )
}
